import { includes, map } from 'lodash';
import React from 'react';
import styled from '@emotion/styled';
import Image from 'next/image';

import Icon, { type IconName } from '@/components/icons';
import { RATERS, COLORS } from '@/constants';
import { ExtraSmall } from '@/components/type';
import Flex from '@/components/grid/flex';
import AncapLogo from '@/public/images/ancap-logo.svg';
import UcsrLogo from '@/public/images/ucsr-logo.svg';
import { shouldForwardProp } from '@/utils/should-forward-prop';

import {
  LogoImageWrapper,
  StarRatingWrapper,
  StarsWrapper,
  Stars,
  RatingLabelWrapper,
  AncapTestYearLabel,
  AncapTestYear,
  OverallSafetyLabel,
} from './styles';
import { STAR_PLACEMENT, type StarPlacement } from './constants';
import { type Rater } from '@/constants/raters';

const LOGO_IMAGE_BY_RATER = {
  [RATERS.ANCAP]: {
    src: AncapLogo,
    alt: 'rated by ANCAP',
  },
  [RATERS.UCSR]: {
    src: UcsrLogo,
    alt: 'rated by UCSR',
  },
};

type StarRatingProps = {
  placement: StarPlacement;
  ratingBy?: Rater;
  stars?: number;
  className?: string;
  forceZeroStars?: boolean;
  testYear?: number;
  starColor?: string;
  maxStars?: number;
  starAriaDescription?: string;
};

const StarRating = ({
  stars,
  ratingBy,
  placement,
  className,
  forceZeroStars = false,
  testYear,
  starColor,
  maxStars = 5,
  starAriaDescription = undefined,
}: StarRatingProps) => {
  const noProvider = !ratingBy || ratingBy === RATERS.NO_RATING || ratingBy === RATERS.NO_CURRENT_RATING;

  const validStars = stars && stars >= 1 && stars <= maxStars;

  const starsArray: IconName[] = [];
  for (let iteration = 1; iteration <= maxStars; iteration++) {
    if (validStars) {
      if (stars >= iteration) {
        starsArray.push('star');
      } else {
        starsArray.push('starDefault');
      }
    } else {
      starsArray.push('starOutline');
    }
  }

  const isNotRated = includes([RATERS.NO_CURRENT_RATING, RATERS.NO_RATING], ratingBy);

  const showRatingProviderLogo =
    ratingBy && !noProvider && (validStars || forceZeroStars) && placement !== STAR_PLACEMENT.SAFETY_ROW;

  const hasAlternateStyles =
    isNotRated || placement === STAR_PLACEMENT.SAFETY_ROW || placement === STAR_PLACEMENT.COMPARISON_CARD_ECO_DATA;

  return (
    <Flex
      flexDirection="column"
      alignItems={hasAlternateStyles ? 'flex-start' : 'center'}
      maxWidth="100%"
      role="img"
      aria-label={`${starAriaDescription ?? ratingBy} - ${stars} out of ${maxStars} stars`}
      tabIndex={0}
      className={className}
    >
      {showRatingProviderLogo && (ratingBy === RATERS.ANCAP || ratingBy === RATERS.UCSR) && (
        <LogoImageWrapper ratingBy={ratingBy}>
          <Image
            src={LOGO_IMAGE_BY_RATER[ratingBy]?.src}
            alt={LOGO_IMAGE_BY_RATER[ratingBy]?.alt}
            // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ src: any; alt: string; placement: any; layout: string; }' is not assignable to type 'IntrinsicAttributes & Omit<DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>, "height" | ... 5 more ... | "srcSet"> & { ...; } & RefAttributes<...>'.
            placement={placement}
            layout="responsive"
          />
        </LogoImageWrapper>
      )}
      <StarRatingWrapper ratingBy={ratingBy}>
        {!includes(
          [STAR_PLACEMENT.COMPARISON_TILE, STAR_PLACEMENT.COMPARISON_CARD, STAR_PLACEMENT.SAFETY_ROW],
          placement
        ) && <RatingByProvider ratingBy={ratingBy} testYear={testYear} />}
        {stars === 0 && forceZeroStars ? (
          <ExtraSmall as="span">ZERO STARS</ExtraSmall>
        ) : (
          <StarsWrapper showBorder={ratingBy === RATERS.ANCAP} noPadding={hasAlternateStyles} placement={placement}>
            {map(starsArray, (star, index) => (
              <Stars placement={placement} key={index}>
                <StyledIcon name={star} color={starColor} />
              </Stars>
            ))}
          </StarsWrapper>
        )}
      </StarRatingWrapper>
    </Flex>
  );
};

const StyledIcon = styled(Icon, { shouldForwardProp })`
  color: ${
    /* @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'ratingBy' does not exist on type 'Pick<IconProps, "string" | "clipPath" | "color" | "cursor" | "direction" | "display" | "filter" | "fontFamily" | "fontSize" | "fontSizeAdjust" | "fontStretch" | "fontStyle" | ... 471 more ... | "ariaLabel"> & { ...; } & { ...; }'. */
    ({ ratingBy, color }) => {
      if (color) {
        return color;
      }

      if (ratingBy === RATERS.UCSR) {
        return COLORS.BRAND_BLUE_TWELVE;
      }

      return COLORS.BLACK;
    }
  };
`;

// @ts-expect-error [🤖 PLEASE FIX 🤖]: Binding element 'ratingBy' implicitly has an 'any' type.
const RatingByProvider = ({ ratingBy, testYear }) => {
  if (ratingBy === RATERS.ANCAP && testYear) {
    return (
      <RatingLabelWrapper ratingBy={ratingBy}>
        <AncapTestYearLabel>Tested</AncapTestYearLabel>
        <AncapTestYear>{testYear}</AncapTestYear>
      </RatingLabelWrapper>
    );
  }
  if (ratingBy === RATERS.UCSR) {
    return (
      <RatingLabelWrapper ratingBy={ratingBy}>
        <OverallSafetyLabel>Overall Safety</OverallSafetyLabel>
      </RatingLabelWrapper>
    );
  }
  return null;
};

export default StarRating;
